import React, { useRef, useState } from 'react';

import { uiConfig } from '@/config';
import { cn } from '@/libs';

import { DeleteGroupItemModal, FormFieldMapper, GroupTitle } from '.';
import { useFieldDeclarations } from '../hooks';
import { type ArrayFormField } from '../typings';
import { FieldDeclarationAlert } from './presenters/FieldDeclarationAlert';

import './ArrayContainer.scss';

type ArrayContainerProps = {
  groupTitle: string;
  groupSubtitle?: string;
  minRequiredHint?: string;
  /** Called once the provider confirms removal, not when the trash icon is clicked. */
  onDelete?: () => void | Promise<void>;
  /** The answers the removal would clear, asked for when the trash icon is clicked; undefined cancels the removal. */
  loadDeleteImpact?: () => Promise<string[] | undefined>;
  fieldsArray: ArrayFormField;
  isCollapsible?: boolean;
  isCollapsed?: boolean;
  onCollapse?: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  allowRemove?: boolean;
  beforeChildren?: React.ReactNode;
  afterChildren?: React.ReactNode;
};

export const ArrayContainer: React.FC<ArrayContainerProps> = ({
  groupTitle,
  groupSubtitle,
  minRequiredHint,
  onDelete,
  loadDeleteImpact,
  fieldsArray,
  isCollapsible,
  isCollapsed,
  onCollapse,
  disabled,
  allowRemove = false,
  beforeChildren,
  afterChildren,
}) => {
  // The answers the open modal lists; undefined while it is closed.
  const [deleteImpact, setDeleteImpact] = useState<string[]>();
  const loadingImpact = useRef(false);
  const isDeleteModalOpen = deleteImpact !== undefined;

  const closeDeleteModal = () => setDeleteImpact(undefined);

  const openDeleteModal = async () => {
    if (!loadDeleteImpact) {
      setDeleteImpact([]);
      return;
    }

    // A second click while the first is asking would send another request, and its late answer could reopen the modal after Cancel.
    if (loadingImpact.current) return;
    loadingImpact.current = true;
    try {
      const impact = await loadDeleteImpact();
      // Without the list the provider can't see what the removal clears, so it doesn't happen.
      if (impact !== undefined) setDeleteImpact(impact);
    } finally {
      loadingImpact.current = false;
    }
  };

  const confirmDelete: React.MouseEventHandler<HTMLButtonElement> = () => {
    closeDeleteModal();
    onDelete?.();
  };

  const declarations = useFieldDeclarations(fieldsArray.fields);

  const groupContainerClasses = cn('array-container__group-container', {
    'array-container__group-container--collapsed': isCollapsed,
  });

  const arrayContainerClasses = cn(
    'grid',
    'gap-x-[20px]',
    'gap-y-[35px]',
    'array-container__row',
    uiConfig.grid.columnsClass,
    { 'array-container__row--collapsed': isCollapsed },
  );

  return (
    <div className={groupContainerClasses}>
      <GroupTitle
        title={groupTitle}
        subtitle={groupSubtitle}
        minRequiredHint={minRequiredHint}
        hasDelete={!disabled}
        onDelete={() => void openDeleteModal()}
        isCollapsible={isCollapsible}
        isCollapsed={isCollapsed}
        onCollapse={onCollapse}
        allowRemove={allowRemove}
      />
      {beforeChildren}
      <div className={arrayContainerClasses}>
        {fieldsArray.fields?.map(field => (
          <React.Fragment key={field.fieldId}>
            <FormFieldMapper field={field} />
            {!disabled &&
              declarations
                .filter(d => d.anchorFieldId === field.fieldId)
                .map(d => <FieldDeclarationAlert key={d.message} message={d.message} />)}
          </React.Fragment>
        ))}
      </div>
      {afterChildren}
      {isDeleteModalOpen && (
        <DeleteGroupItemModal
          title={groupTitle}
          isOpen
          affectedAnswers={deleteImpact}
          onCancel={closeDeleteModal}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
};
